
import { useState, useCallback } from 'react';
import type { Node, Edge } from '@xyflow/react';
import type { DeleteTarget } from '../DeleteConfirmationDialog';

interface UseDeleteConfirmationConfig<N extends Record<string, unknown>, E extends Record<string, unknown>> {
    /** Get the label for a node */
    getNodeLabel?: (node: Node<N>) => string;
    /** Get the type name for a node */
    getNodeType?: (node: Node<N>) => string;
    /** Get connected edge count for a node */
    getConnectedEdgeCount?: (nodeId: string, edges: Edge<E>[]) => number;
    /** Callback to actually perform deletion */
    onDelete: (nodes: Node<N>[], edges: Edge<E>[]) => Promise<void>;
}

export function useDeleteConfirmation<N extends Record<string, unknown>, E extends Record<string, unknown>>({
    getNodeLabel = (node) => (node.data as Record<string, unknown>)?.label as string ?? node.id,
    getNodeType = (node) => node.type ?? 'state',
    getConnectedEdgeCount = (nodeId, edges) =>
        edges.filter((e) => e.source === nodeId || e.target === nodeId).length,
    onDelete,
}: UseDeleteConfirmationConfig<N, E>) {
    const [isOpen, setIsOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [pendingNodes, setPendingNodes] = useState<Node<N>[]>([]);
    const [pendingEdges, setPendingEdges] = useState<Edge<E>[]>([]);
    const [allEdges, setAllEdges] = useState<Edge<E>[]>([]);

    /** Request deletion of nodes/edges - shows confirmation dialog */
    const requestDelete = useCallback(
        (nodes: Node<N>[], edges: Edge<E>[], currentEdges: Edge<E>[]) => {
            setPendingNodes(nodes);
            setPendingEdges(edges);
            setAllEdges(currentEdges);
            setIsOpen(true);
        },
        []
    );

    /** Confirm and perform deletion */
    const confirmDelete = useCallback(async () => {
        setIsDeleting(true);
        try {
            await onDelete(pendingNodes, pendingEdges);
        } finally {
            setIsDeleting(false);
            setIsOpen(false);
            setPendingNodes([]);
            setPendingEdges([]);
        }
    }, [pendingNodes, pendingEdges, onDelete]);

    /** Cancel deletion */
    const cancelDelete = useCallback(() => {
        setIsOpen(false);
        setPendingNodes([]);
        setPendingEdges([]);
    }, []);

    /** Build DeleteTarget array for dialog */
    const targets: DeleteTarget[] = [
        ...pendingNodes.map((node) => ({
            type: 'node' as const,
            id: node.id,
            label: getNodeLabel(node),
            nodeType: getNodeType(node),
            connectedEdges: getConnectedEdgeCount(node.id, allEdges),
        })),
        ...pendingEdges.map((edge) => ({
            type: 'edge' as const,
            id: edge.id,
            label: (edge.data as Record<string, unknown>)?.label as string | undefined,
        })),
    ];

    return {
        isOpen,
        isDeleting,
        targets,
        requestDelete,
        confirmDelete,
        cancelDelete,
        setIsOpen,
    };
}

